import { useMemo, useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Icon } from "@/components/ui/Icon";
import { ErrorState } from "@/components/ui/States";
import { useList } from "@/lib/api/queries";
import { useReport } from "@/lib/api/reports";
import { qty } from "@/lib/format";
import { cellTypeLabel, functionLabel, zoneAreaLabel, zoneLabel } from "./labels";
import "./topology.css";

/**
 * Topologiya va sig'im.
 *
 * Ilgari bu umumiy HISOBOT edi: ustunlar server bergan kalitlar
 * bilan chizilar, xodim esa `A-STORAGE`, `Y1`, `is_active` kabi
 * sxema nomlarini o'qirdi. Endi ekran ombor tilida gapiradi va
 * yacheykani tanlab, ichida nima turganini ko'rsatadi.
 */

interface CapacityRow {
  location_id: string;
  location_code: string;
  warehouse_code: string;
  zone_code: string;
  location_function: string;
  cell_type: string;
  capacity_pallets: string;
  occupied_slots: string;
  free_slots: string;
  max_weight_kg: string;
  stored_weight_kg: string;
  weight_fits: string;
  distinct_sku_count: string;
  putaway_allowed: string;
  is_active: string;
  blocked: string;
  abc_zone: string;
}

/** Yacheyka turiga bog'langan chegaralar (PAR-02). */
interface CellDefault {
  cell_type: string;
  max_weight_kg?: string;
  max_sku_count?: number;
}

// Serverning chegarasi (`httpx.MaxLimit`). Zona va vazifa
// SERVERDA kesilgani uchun bitta sahifa odatda yetarli; yetmasa
// jadval ostida ochiq aytiladi.
const PAGE_SIZE = 500;

/** «ha» / «yo'q» matnini mantiqiy qiymatga aylantiradi. */
const yes = (value: string) => value === "ha" || value === "true";

/**
 * Yacheyka holati — BITTA ustun.
 *
 * Ilgari «Faol» va «Bloklangan» alohida ustun edi va ularni
 * birgalikda o'qish kerak bo'lardi: faol, lekin bloklangan katak
 * ikkita «ha» bo'lib ko'rinardi. Endi holat bitta so'z va rang.
 */
type State = "blocked" | "closed" | "full" | "free" | "busy";

function stateOf(row: CapacityRow): State {
  if (yes(row.blocked)) return "blocked";
  if (!yes(row.is_active)) return "closed";
  const free = Number(row.free_slots || 0);
  const occupied = Number(row.occupied_slots || 0);
  if (occupied > 0 && free <= 0) return "full";
  if (occupied > 0) return "busy";
  return "free";
}

const STATE_LABEL: Record<State, string> = {
  blocked: "Bloklangan",
  closed: "O'chirilgan",
  full: "To'la",
  busy: "Band",
  free: "Bo'sh",
};

export function Topology() {
  const [filters, setFilters] = useState({
    q: "",
    warehouse: "",
    func: "",
    cellType: "",
    area: "",
    state: "" as "" | State,
    onlyPutaway: false,
  });
  const [picked, setPicked] = useState<CapacityRow | null>(null);

  // Zona va vazifa SERVERDA filtrlanadi: reyestrda minglab katak
  // bor va brauzerga yuklangan sahifani kesish «B ombor» ni
  // umuman topolmaslikka olib kelardi.
  const report = useReport("location-capacity", {
    limit: PAGE_SIZE,
    zone_code: filters.warehouse || undefined,
    location_function: filters.func || undefined,
  });
  const defaults = useList<CellDefault>("topology", "/api/v1/cell-type-defaults");
  // Zonalar ro'yxati ALOHIDA: sahifadagi kataklardan yig'ilsa,
  // birinchi sahifada uchramagan korpus filtrda ko'rinmasdi.
  const zones = useList<{ code: string }>("topology", "/api/v1/zones", { limit: 200 });

  const rows = useMemo(
    () => (report.data?.items ?? []) as unknown as CapacityRow[],
    [report.data],
  );

  // Filtr qiymatlari MA'LUMOTDAN yig'iladi: qo'lda yozilgan
  // ro'yxat reyestr o'zgarganda eskirib qolardi.
  const options = useMemo(() => {
    const unique = (pick: (row: CapacityRow) => string) =>
      [...new Set(rows.map(pick).filter(Boolean))].sort();
    return {
      // Qolgan filtrlar yuklangan sahifadan: ular server
      // tomonidan qo'llab-quvvatlanmaydi va kesim allaqachon
      // zona/vazifa bilan toraytirilgan.
      cellTypes: unique((row) => row.cell_type),
      areas: unique((row) => row.abc_zone),
    };
  }, [rows]);

  const zoneCodes = useMemo(
    () => (zones.data?.items ?? []).map((zone) => zone.code).sort(),
    [zones.data],
  );
  const functions = useMemo(
    () => [...new Set(rows.map((row) => row.location_function).filter(Boolean))].sort(),
    [rows],
  );

  const limits = useMemo(
    () => new Map((defaults.data?.items ?? []).map((item) => [item.cell_type, item])),
    [defaults.data],
  );

  const visible = useMemo(() => {
    const needle = filters.q.trim().toLowerCase();
    return rows.filter((row) => {
      // Zona va vazifa SERVERDA kesilgan — bu yerda qaytarilmaydi.
      if (needle && !row.location_code.toLowerCase().includes(needle)) return false;
      if (filters.cellType && row.cell_type !== filters.cellType) return false;
      if (filters.area && row.abc_zone !== filters.area) return false;
      if (filters.state && stateOf(row) !== filters.state) return false;
      if (filters.onlyPutaway && !yes(row.putaway_allowed)) return false;
      return true;
    });
  }, [rows, filters]);

  const columns: Array<Column<CapacityRow>> = [
    {
      key: "code",
      header: "Katak",
      render: (row) => <span className="mono">{row.location_code}</span>,
    },
    {
      key: "warehouse",
      header: "Ombor",
      render: (row) => zoneLabel(row.zone_code),
    },
    {
      key: "area",
      header: "Hudud",
      render: (row) => zoneAreaLabel(row.abc_zone),
    },
    {
      key: "function",
      header: "Vazifa",
      render: (row) => functionLabel(row.location_function),
    },
    { key: "type", header: "Turi", render: (row) => cellTypeLabel(row.cell_type) },
    {
      key: "state",
      header: "Holat",
      render: (row) => {
        const state = stateOf(row);
        return <span className={`cell-state ${state}`}>{STATE_LABEL[state]}</span>;
      },
    },
    {
      key: "slots",
      header: "Joy",
      align: "right",
      render: (row) =>
        row.capacity_pallets ? (
          <span className="num">
            {row.occupied_slots} / {row.capacity_pallets}
          </span>
        ) : (
          // Mayda terish yacheykasida pallet o'rni yo'q (TZ §5.2).
          <span className="muted">pallet emas</span>
        ),
    },
    {
      key: "weight",
      header: "Vazn, kg",
      align: "right",
      render: (row) => {
        // Chegara: avval yacheykaning O'Z qiymati, bo'lmasa
        // turning standarti (PAR-02). Serverdagi `weight_fits`
        // faqat yacheykaning o'z qiymatini biladi va Y1 kabi
        // o'lchanmagan kataklar uchun doim «sig'adi» derdi.
        // Reyestrda chegara ko'rsatilmagan katak «0» bilan
        // keladi — bu nol kilogramm degani EMAS. Noldan katta
        // qiymat bo'lmasa turning standartiga tushamiz.
        const own = Number(row.max_weight_kg || 0) > 0 ? row.max_weight_kg : "";
        const limit = own || limits.get(row.cell_type)?.max_weight_kg || "";
        const over = limit
          ? Number(row.stored_weight_kg || 0) > Number(limit)
          : !yes(row.weight_fits);
        return (
          <span className={`num${over ? " over" : ""}`}>
            {qty(row.stored_weight_kg)}
            {limit ? ` / ${qty(limit)}` : ""}
          </span>
        );
      },
    },
    {
      key: "sku",
      header: "SKU",
      align: "right",
      render: (row) => {
        // Chegara turdan keladi (PAR-02); berilmagan bo'lsa bitta
        // (LOC-001).
        const limit = limits.get(row.cell_type)?.max_sku_count ?? 1;
        const used = Number(row.distinct_sku_count || 0);
        return (
          <span className={`num${used > limit ? " over" : ""}`}>
            {used} / {limit}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <button
          type="button"
          className="btn btn-text btn-sm"
          onClick={() => setPicked(row)}
        >
          <Icon name="search" size={14} />
          Ichida nima bor
        </button>
      ),
    },
  ];

  const set = (patch: Partial<typeof filters>) =>
    setFilters((current) => ({ ...current, ...patch }));

  return (
    <>
      <PageHeader
        title="Topologiya va sig'im"
        hint="Ombor kataklari: qayerda, nima uchun, qancha joy va vazn bor. Katakni tanlasangiz ichidagi tovarlar ko'rinadi."
      />

      {report.error ? (
        <ErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <>
          <div className="topo-filters">
            <div className="search-box topo-search">
              <Icon name="search" size={16} className="icon-inside" />
              <input
                className="input"
                placeholder="Katak kodi…"
                value={filters.q}
                onChange={(event) => set({ q: event.target.value })}
              />
            </div>

            <Picker label="Ombor" value={filters.warehouse} options={zoneCodes}
              render={zoneLabel} onChange={(value) => set({ warehouse: value })} />
            <Picker label="Hudud" value={filters.area} options={options.areas}
              render={zoneAreaLabel} onChange={(value) => set({ area: value })} />
            <Picker label="Vazifa" value={filters.func} options={functions}
              render={functionLabel} onChange={(value) => set({ func: value })} />
            <Picker label="Turi" value={filters.cellType} options={options.cellTypes}
              render={cellTypeLabel} onChange={(value) => set({ cellType: value })} />
            <Picker label="Holat" value={filters.state}
              options={Object.keys(STATE_LABEL)}
              render={(value) => STATE_LABEL[value as State]}
              onChange={(value) => set({ state: value as State })} />

            <label className="topo-check">
              <input
                type="checkbox"
                checked={filters.onlyPutaway}
                onChange={(event) => set({ onlyPutaway: event.target.checked })}
              />
              <span>Joylashtirishga ruxsat</span>
            </label>

            <button
              type="button"
              className="btn btn-text btn-sm"
              onClick={() =>
                setFilters({
                  q: "", warehouse: "", func: "",
                  cellType: "", area: "", state: "", onlyPutaway: false,
                })
              }
            >
              Tozalash
            </button>
          </div>

          <DataTable
            tableId="topology-capacity"
            columns={columns}
            rows={visible}
            rowKey={(row) => row.location_id}
            isLoading={report.isLoading}
            emptyTitle="Katak topilmadi"
            emptyHint="Filtrni bo'shating yoki reyestrni Import bo'limidan yuklang."
            hasMore={report.data?.has_more}
            footer={
              <span>
                {visible.length} katak
                {visible.length !== rows.length ? ` (jami ${rows.length})` : ""}
              </span>
            }
          />
        </>
      )}

      {picked && <CellContents row={picked} onClose={() => setPicked(null)} />}
    </>
  );
}

/** Bitta qiymatli tanlov: bo'sh qiymat — «hammasi». */
function Picker({
  label,
  value,
  options,
  render,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  render?: (value: string) => string;
  onChange: (value: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <label className="topo-field">
      <span>{label}</span>
      <select className="input" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Hammasi</option>
        {options.map((item) => (
          <option key={item} value={item}>
            {render ? render(item) : item}
          </option>
        ))}
      </select>
    </label>
  );
}

interface StockRow {
  sku_article: string;
  sku_name: string;
  lp_id: string;
  lot_ref: string;
  quality_status: string;
  on_hand_qty: string;
  reserved_qty: string;
  available_qty: string;
}

/**
 * Yacheyka ichidagi tovarlar.
 *
 * Qoldiq hisobotidan katak bo'yicha olinadi — ro'yxat ekranida
 * bu ma'lumot yo'q va uni har bir qator uchun oldindan yuklash
 * yuzlab so'rov bo'lardi.
 */
function CellContents({ row, onClose }: { row: CapacityRow; onClose: () => void }) {
  // Filtr SERVERDA: katakni brauzerda ajratish birinchi
  // sahifadan tashqaridagi qoldiqni butunlay ko'rsatmasdi.
  const stock = useReport("stock", { location_id: row.location_id, limit: 200 });
  const items = (stock.data?.items ?? []) as unknown as StockRow[];

  return (
    <Drawer label="Katak tarkibi">
      <div className="drawer-head">
        <div>
          <div className="drawer-title mono">{row.location_code}</div>
          <div className="drawer-sub">
            {zoneLabel(row.zone_code)} · {functionLabel(row.location_function)} ·{" "}
            {cellTypeLabel(row.cell_type)}
          </div>
        </div>
        <button type="button" className="btn btn-text btn-sm" onClick={onClose}>
          <Icon name="close" size={16} />
        </button>
      </div>

      <div className="drawer-body">
        {stock.isLoading ? (
          <div className="skeleton" style={{ height: 160 }} />
        ) : items.length === 0 ? (
          <p className="field-hint">Katak bo'sh.</p>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Tovar</th>
                <th className="right">Qoldiq</th>
                <th className="right">Band</th>
                <th className="right">Mavjud</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={`${item.sku_article}-${item.lot_ref}-${index}`}>
                  <td>
                    <div className="mono">{item.sku_article}</div>
                    <div className="muted">{item.sku_name}</div>
                  </td>
                  <td className="right num">{qty(item.on_hand_qty)}</td>
                  <td className="right num">{qty(item.reserved_qty)}</td>
                  <td className="right num">{qty(item.available_qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Drawer>
  );
}
